import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { AuthProvider, useAuth } from './auth/AuthProvider';
import { PrincipalContextProvider } from './context/PrincipalContext';
import { EntitlementsProvider } from './context/EntitlementsContext';
import { PermissionsProvider } from './context/PermissionsContext';
import { setTokenGetter } from './api/client';
import LoginPage from './auth/LoginPage';
import { CadenceThemeProvider } from './theme/ThemeContext';
import AppLayout from './components/AppLayout';
import DashboardPage from './pages/DashboardPage';
import DashboardBriefingPage from './pages/DashboardBriefingPage';
import UnifiedPlanPage from './pages/UnifiedPlanPage';
import EaLandingPage from './pages/EaLandingPage';
import { useDashboardVariant } from './hooks/useDashboardVariant';
import CalendarPage from './pages/CalendarPage';
import StrategyPage from './pages/StrategyPage';
import ImagesPage from './pages/ImagesPage';
import ContextPage from './pages/ContextPage';
import PreferencesPage from './pages/PreferencesPage';
import FocusTimePage from './pages/FocusTimePage';
import ReportsPage from './pages/ReportsPage';
import { TranscriptsPage } from './pages/TranscriptsPage';
import EAWorkspacePage from './pages/EAWorkspacePage';
import TenantBillingPage from './pages/TenantBillingPage';
import TenantOrganisationPage from './pages/TenantOrganisationPage';
import SignupPage from './pages/SignupPage';
import OnboardingSuccessPage from './pages/OnboardingSuccessPage';
import OAuthCallback from './auth/OAuthCallback';
import MagicLinkCallback from './auth/MagicLinkCallback';
import MfaSetupPage from './pages/MfaSetupPage';
import PasswordResetPage from './pages/PasswordResetPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import PrincipalProfilePage from './pages/PrincipalProfilePage';
import WebhooksPage from './pages/WebhooksPage';
import ReflectionsPage from './pages/ReflectionsPage';
import FeatureRequestPage from './pages/FeatureRequestPage';
import ProjectsPage from './pages/ProjectsPage';
import ProjectDetailPage from './pages/ProjectDetailPage';
import IdeasPage from './pages/IdeasPage';
import ConnectionsPage from './pages/ConnectionsPage';
import DecisionsPage from './pages/DecisionsPage';
import AuditLogPage from './pages/AuditLogPage';
import HorizonPage from './pages/HorizonPage';
import DelegationPage from './pages/DelegationPage';
import OAuthAuthorizePage from './pages/OAuthAuthorizePage';
import DesktopAuthConsentPage from './pages/DesktopAuthConsentPage';

// WorkItemsPage is ~1500 LOC; lazy-loaded so a user landing on the
// dashboard doesn't pay for its bundle until they navigate to /work-items.
const WorkItemsPage = lazy(() => import('./pages/WorkItemsPage'));
// Used once per person, so it stays out of the main bundle.
const OnboardingWizardPage = lazy(() => import('./pages/OnboardingWizardPage'));
const SetupPage = lazy(() => import('./pages/SetupPage'));
const SetupHealthPage = lazy(() => import('./pages/SetupHealthPage'));
const JoinPage = lazy(() => import('./pages/JoinPage'));

// Admin / platform / prompt-management pages are lazy-loaded. The executive
// majority never opens them, so shipping code they will not run is dead
// weight on first paint.
const PromptManagementPage = lazy(() => import('./pages/PromptManagementPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const PlatformOverviewPage = lazy(() => import('./pages/PlatformOverviewPage'));
const PlatformTenantsPage = lazy(() => import('./pages/PlatformTenantsPage'));
const PlatformProductsPage = lazy(() => import('./pages/PlatformProductsPage'));
const PlatformEmailPage = lazy(() => import('./pages/PlatformEmailPage'));
const PlatformSecurityPage = lazy(() => import('./pages/PlatformSecurityPage'));
const PlatformBugReportsPage = lazy(() => import('./pages/PlatformBugReportsPage'));
const PlatformBillingPage = lazy(() => import('./pages/PlatformBillingPage'));
const PlatformReleasesPage = lazy(() => import('./pages/PlatformReleasesPage'));
const CapacityPage = lazy(() => import('./pages/CapacityPage'));
const CloseDayPage = lazy(() => import('./pages/CloseDayPage'));
const BriefPage = lazy(() => import('./pages/BriefPage'));

function AuthGate({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, ensureValidToken } = useAuth();

  useEffect(() => {
    setTokenGetter(ensureValidToken);
  }, [ensureValidToken]);

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  return <>{children}</>;
}

// Route "/" based on user role and the dashboard variant preference.
// Localstorage key the Brief writes when the user clicks "Enter the
// workspace" — same-day visits skip the cinematic landing and go
// straight to the dashboard. Resets at midnight automatically because
// the value is the YYYY-MM-DD the brief was dismissed on.
const BRIEF_DISMISSED_KEY = 'cadence_brief_dismissed_date';

function todayYMD(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function briefSeenToday(): boolean {
  try { return localStorage.getItem(BRIEF_DISMISSED_KEY) === todayYMD(); }
  catch { return true; } // private browsing — skip the Brief rather than loop
}

// Role keys are case-insensitive so a backend that ever lowercases
// the role string (or a cached session from an earlier app version)
// still routes correctly.
function normalizedRole(role: string | undefined | null): string {
  return (role ?? '').trim().toLowerCase();
}
function isPlatformAdmin(user: { role?: string } | null | undefined): boolean {
  return normalizedRole(user?.role) === 'platformadmin';
}
// CLASSIFICATION (what surface do you live on?) — role string only.
// Do NOT include the `isTenantAdmin` permission flag here. The flag means
// "you can do tenant-admin THINGS" (manage users, billing, etc.); it does
// NOT mean "your home page is the admin dashboard". Executives who also
// own the tenant (typical for the platform-owner-as-executive case) have
// role=Executive AND isTenantAdmin=true — they still get the Brief.
// Bug 2026-06-01: prior version conflated the flag with the role, locking
// Executive-with-admin-perms users out of /brief both on login redirect
// and when clicking the menu link.
function isAdminRole(user: { role?: string } | null | undefined): boolean {
  const r = normalizedRole(user?.role);
  return r === 'tenantadmin' || r === 'admin';
}

function BriefRoute() {
  const { user } = useAuth();
  // PlatformAdmin / TenantAdmin / generic Admin don't have an
  // Executive Brief — they have operational dashboards instead.
  // Anyone non-Executive landing here gets bounced via "/" so
  // RoleBasedHome picks the right surface for their role.
  if (isPlatformAdmin(user) || isAdminRole(user)) {
    return <Navigate to="/" replace />;
  }
  return <BriefPage />;
}

function RoleBasedHome() {
  const { user } = useAuth();
  const { variant } = useDashboardVariant();
  // A guided-setup link (`/?setup=next`, from setup's last step or the
  // emailed link) opens a step drawer on the workspace; don't send it via
  // The Brief, which would drop the query.
  const location = useLocation();
  // SetupProvider drops the param once it opens the drawer and leaves
  // `setupLink` in the history state, so the page stays put afterwards.
  const opensSetup = new URLSearchParams(location.search).has('setup')
    || !!(location.state as { setupLink?: boolean } | null)?.setupLink;
  // Guided first-run gate (spec §0). The onboarding status is bootstrapped from
  // /auth/me after login: `undefined` = not yet resolved, `null` = not onboarded,
  // a date string = done. We must WAIT on `undefined` before any role routing —
  // otherwise an Executive would be sent to /brief before we know they still
  // need setup. `null` → setup. (Existing users were backfilled, so they resolve
  // to a date and only see a sub-second loader on the first home view.)
  const onboardingStatus = (user as { onboardingCompletedAt?: string | null } | null)?.onboardingCompletedAt;
  if (user && onboardingStatus === undefined) {
    return <LazyRouteFallback />;
  }
  if (user && onboardingStatus === null) {
    return <Navigate to="/getting-started" replace />;
  }
  // Platform admins manage tenants — they never get the Brief.
  if (isPlatformAdmin(user)) return <PlatformOverviewPage />;
  if (normalizedRole(user?.role) === 'executiveassistant') return <EaLandingPage />;
  // Tenant admins / generic Admins skip the Brief too — they need the
  // operational dashboard, not the cinematic landing. Only Executives
  // get the Brief moment.
  if (isAdminRole(user)) {
    return variant === 'briefing' ? <DashboardBriefingPage /> : <DashboardPage />;
  }
  // Executives land on The Brief once per day. Clicking "Enter the
  // workspace" writes today's date to localStorage; subsequent visits
  // skip straight to the dashboard.
  if (!briefSeenToday() && !opensSetup) return <Navigate to="/brief" replace />;
  return variant === 'briefing' ? <DashboardBriefingPage /> : <DashboardPage />;
}

function LazyRouteFallback() {
  return (
    <div className="flex min-h-[200px] items-center justify-center">
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
    </div>
  );
}

function ProtectedApp() {
  return (
    <AuthProvider>
      {/* Plan entitlements load once per sign-in, alongside /auth/me, and
          decide which nav items and pages the tenant sees; the user's own
          permissions then hide what an admin has set to No. */}
      <EntitlementsProvider>
      <PermissionsProvider>
      <PrincipalContextProvider>
      <AuthGate>
        <Suspense fallback={<LazyRouteFallback />}>
        <Routes>
          <Route element={<AppLayout />}>
            <Route path="/" element={<RoleBasedHome />} />
            <Route path="/mfa-setup" element={<MfaSetupPage />} />
            <Route path="/getting-started" element={<OnboardingWizardPage />} />
            <Route path="/setup" element={<SetupPage />} />
            <Route path="/setup/health" element={<SetupHealthPage />} />
            <Route path="/plan" element={<UnifiedPlanPage />} />
            <Route path="/focus" element={<FocusTimePage />} />
            <Route path="/close-day" element={<CloseDayPage />} />
            <Route path="/capacity" element={<CapacityPage />} />
            <Route path="/work-items" element={<WorkItemsPage />} />
            <Route path="/calendar" element={<CalendarPage />} />
            <Route path="/strategy" element={<StrategyPage />} />
            <Route path="/projects" element={<ProjectsPage />} />
            <Route path="/projects/:projectId" element={<ProjectDetailPage />} />
            <Route path="/ideas" element={<IdeasPage />} />
            <Route path="/images" element={<ImagesPage />} />
            <Route path="/transcripts" element={<TranscriptsPage />} />
            <Route path="/context" element={<ContextPage />} />
            <Route path="/preferences" element={<PreferencesPage />} />
            <Route path="/reports" element={<ReportsPage />} />
            <Route path="/horizon" element={<HorizonPage />} />
            <Route path="/delegation" element={<DelegationPage />} />
            <Route path="/reflections" element={<ReflectionsPage />} />
            <Route path="/ea-workspace" element={<EAWorkspacePage />} />
            <Route path="/principal-profile" element={<PrincipalProfilePage />} />
            <Route path="/principal-profile/:execId" element={<PrincipalProfilePage />} />
            <Route path="/webhooks" element={<WebhooksPage />} />
            <Route path="/connections" element={<ConnectionsPage />} />
            <Route path="/decisions" element={<DecisionsPage />} />
            <Route path="/audit-log" element={<AuditLogPage />} />
            <Route path="/feature-requests" element={<FeatureRequestPage />} />
            <Route path="/prompt-management" element={<PromptManagementPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/billing" element={<TenantBillingPage />} />
            <Route path="/organisation" element={<TenantOrganisationPage />} />
            <Route path="/platform" element={<PlatformOverviewPage />} />
            <Route path="/platform/tenants" element={<PlatformTenantsPage />} />
            <Route path="/platform/products" element={<PlatformProductsPage />} />
            <Route path="/platform/email" element={<PlatformEmailPage />} />
            <Route path="/platform/security" element={<PlatformSecurityPage />} />
            <Route path="/platform/bug-reports" element={<PlatformBugReportsPage />} />
            <Route path="/platform/billing" element={<PlatformBillingPage />} />
            <Route path="/platform/releases" element={<PlatformReleasesPage />} />
          </Route>
          {/* The Brief: rendered outside AppLayout so the cinematic
              dark moment fills the viewport without the sidebar +
              top-bar chrome competing for attention. AuthGate still
              gates on a valid session. PlatformAdmin / TenantAdmin
              never see the Brief — it's an Executive moment — so any
              admin who lands here (deep link, cached URL, stale tab)
              gets bounced to the role-appropriate home via /. */}
          <Route path="/brief" element={<BriefRoute />} />
          {/* OAuth authorize: rendered without AppLayout chrome so the consent
              screen is the only thing the user sees. AuthGate above still gates
              on a valid Cadence session — login round-trips back here on success. */}
          <Route path="/oauth/authorize" element={<OAuthAuthorizePage />} />
          {/* Desktop sign-in consent: opened by the Mac / Windows app inside
              an OS-managed browser session. AuthGate above gates on a valid
              Cadence session — login (and MFA) round-trip back here, so by
              the time this renders the user is signed in and we can mint a
              one-time code for the desktop app to exchange. */}
          <Route path="/oauth/desktop" element={<DesktopAuthConsentPage />} />
          {/* Route aliases for common URL patterns */}
          <Route path="/strategic-drivers" element={<Navigate to="/strategy" replace />} />
          <Route path="/focus-time" element={<Navigate to="/focus" replace />} />
          <Route path="/settings" element={<Navigate to="/preferences" replace />} />
          <Route path="/dashboard" element={<Navigate to="/" replace />} />
          {/* Catch /login and any unknown routes — redirect to dashboard */}
          <Route path="/login" element={<Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </AuthGate>
      </PrincipalContextProvider>
      </PermissionsProvider>
      </EntitlementsProvider>
    </AuthProvider>
  );
}

function AppRouter() {
  // Use React-Router's location so soft navigation (Link / navigate) re-runs
  // this branch decision. Previously this read `window.location.pathname`
  // once at first render, which meant clicking "Back to sign in" from
  // /reset-password or /forgot-password kept the public-auth `<Routes>`
  // subtree mounted and rendered nothing for /login (blank white screen).
  const { pathname } = useLocation();

  // Public routes render WITHOUT the AuthGate redirect, but they still
  // need AuthProvider in the tree so `useAuth()` works — SignupPage and
  // the callback pages call `applyTokens` to sign the user in once their
  // flow completes, and `useAuth()` throws when no provider is mounted.
  // The `dark` class on the wrapper gives the shadcn primitives in
  // these pages a dark palette regardless of the user's saved preference.
  if (pathname === '/signup' || pathname.startsWith('/onboarding') || pathname === '/auth/callback' || pathname === '/auth/magic-link' || pathname === '/reset-password' || pathname === '/forgot-password' || pathname === '/join') {
    return (
      <AuthProvider>
        <div className="dark">
          <Routes>
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/onboarding/success" element={<OnboardingSuccessPage />} />
            <Route path="/auth/callback" element={<OAuthCallback />} />
            <Route path="/auth/magic-link" element={<MagicLinkCallback />} />
            <Route path="/reset-password" element={<PasswordResetPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/join" element={<Suspense fallback={null}><JoinPage /></Suspense>} />
          </Routes>
        </div>
      </AuthProvider>
    );
  }

  return <ProtectedApp />;
}

function App() {
  return (
    <CadenceThemeProvider>
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
    </CadenceThemeProvider>
  );
}

export default App;
